<template>
  <div>
    <b-field label-position="on-border">
      <template slot="label">
        <div class="label__row">
          <div class="label__col">
            {{ label }} ({{ values.length }})
            <a @click.prevent="toggleMultiselect" aria-controls="massSelectList" v-if="levels.length > 1">
              <b-tooltip label="Групповое добавление" position="is-top">
                <span class="icon is-small">
                  <i class="mdi mdi-plus-box-outline"></i>
                </span>
              </b-tooltip>
            </a>
          </div>

          <div class="label__col label__append">
            <a @click.prevent="clearValues" v-if="values.length > 0">
              <b-tooltip label="Очистить" position="is-top">
                <span class="icon is-small">
                  <i class="mdi mdi-close"></i>
                </span>
              </b-tooltip>
            </a>
          </div>
        </div>
      </template>

      <b-autocomplete
        :key="key"
        placeholder="Выберите значения"
        open-on-focus
        clear-on-select
        :data="filteredData"
        field="name"
        @select="addSelectedValue($event, index)"
        @typing="cacheTypedValue($event)"
        ref="autocompleteInput"
        v-model="input"
      >
        <template slot-scope="props">
          {{ props.option.name }}
          <div class="is-size-7 has-text-grey-light" v-if="props.option.internalId">
            [* {{ optionCurrentValue(props.option) }}]
          </div>
        </template>
      </b-autocomplete>
    </b-field>

    <b-collapse :open.sync="isMassSelectOpen" aria-id="massSelectList">
      <div class="block m-b-md m-t-md" v-if="levels.length > 1">
        <div class="buttons" v-for="(level, index) of levels.length - 1" :key="index">
          <template v-for="obj of data">
            <b-button size="is-small" v-if="obj.level === level" :key="obj.name" @click="addItemsFromParent(obj.id)">
              {{ obj.name }}
            </b-button>
          </template>
        </div>
      </div>
    </b-collapse>

    <div class="block m-t-md">
      <b-taglist>
        <draggable v-model="values" ghost-class="is-warning" @start="drag = true" @end="drag = false" @change="moveTag">
          <b-tag
            type="is-primary"
            class="movable"
            size="is-small"
            v-for="(tag, index) of values"
            :key="tag.name"
            closable
            @close="removeSelectedValue(tag, index)"
          >
            <span>
              <template v-if="tag.name.length > 25">
                <b-tooltip :label="tag.name" position="is-top">
                  {{ tag.name.substring(0, 25) }}
                  ...
                </b-tooltip>
              </template>

              <template v-else>{{ tag.name }}</template>

              <b-tooltip class="m-l-sm" type="is-warning" :label="optionCurrentValue(tag)" v-if="tag.internalId">
                <b-icon icon="information-outline" size="is-small" type="is-warning"></b-icon>
              </b-tooltip>
            </span>
          </b-tag>
        </draggable>
      </b-taglist>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  components: {
    draggable
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    field: {
      type: String,
      required: true
    },
    level: {
      type: [String, Number]
    },
    data: {
      type: Array,
      default: () => []
    },
    index: {
      type: Number,
      required: false
    },
    value: {
      type: Array,
      default: () => []
    }
  },

  mounted() {
    for (const value of this.value) {
      const selectedValue = this.data.find(el => el.name === value.name);
      this.values.push(selectedValue);
    }
  },

  data() {
    return {
      key: 0,
      cachedValue: '',
      values: [],
      input: '',
      isMassSelectOpen: false
    };
  },

  computed: {
    filteredData() {
      const filteredData = this.data.filter(item => {
        return !this.values.some(value => value.name === item.name);
      });

      return filteredData.filter(option => {
        if (this.level) {
          return (
            option.name
              .toString()
              .toLowerCase()
              .indexOf(this.input.toLowerCase()) >= 0 && option.level == this.level
          );
        } else {
          return (
            option.name
              .toString()
              .toLowerCase()
              .indexOf(this.input.toLowerCase()) >= 0
          );
        }
      });
    },

    levels() {
      const levels = this.data.filter(el => el.level).map(el => el.level);
      return [...new Set(levels)];
    }
  },

  watch: {
    async values(newValue, oldValue) {
      if (newValue.length > oldValue.length) {
        this.$refs.autocompleteInput.focus();

        await this.$nextTick();
        this.input = this.cachedValue;
      }
    },

    // Force re-render component after empty filter result, that
    // fix bug with hidden drop down list after empty filtered
    filteredData(newValue, oldValue) {
      if (oldValue.length === 0 && newValue !== 0) {
        this.key++;
      }
    }
  },

  methods: {
    optionCurrentValue(option) {
      const optionValue = this.data.find(value => {
        return value.id == option.id && !value.internalId;
      });

      return optionValue.name;
    },

    clearValues() {
      this.values = [];
      this.emitInput();
    },

    toggleMultiselect() {
      this.isMassSelectOpen = !this.isMassSelectOpen;
    },

    addItemsFromParent(id) {
      const fromParent = this.data.filter(el => el.parent_item == id);
      this.values = [...new Set([...fromParent, ...this.values])];
      this.emitInput();
    },

    addSelectedValue(value) {
      if (value) {
        const newValues = [...this.values, value];
        this.values = newValues;

        this.emitInput();
      }
    },

    removeSelectedValue(value, index) {
      this.values = this.values.filter(el => el.name !== value.name);
      this.emitInput();
    },

    moveTag() {
      this.emitInput();
    },

    emitInput() {
      this.$emit('input', this.values);
    },

    cacheTypedValue(e) {
      this.cachedValue = e;
    }
  }
};
</script>

<style lang="scss" scoped>
.label {
  &__row {
    display: flex;
  }

  &__append {
    margin-left: auto;
  }
}

.clear {
  font-weight: normal;
  cursor: pointer;
}

.movable {
  cursor: grab !important;
  user-select: none;
}
</style>
